import { useMemo } from 'react';
import { Marker, Tooltip } from 'react-leaflet';
import { DivIcon } from 'leaflet';
import { LatLngPoint } from '../../utils/route';
import { formatLatLng } from '../../utils/formatters';

interface PlannerHomeBaseMarkerProps {
  /** Where the home pin sits — a custom home, or the default start point. */
  position: LatLngPoint;
  /** True when this is the default home (mission start / waypoint 1), not a
   *  custom home base the operator placed. */
  isDefault: boolean;
  /**
   * True when the home point falls outside the drone's inclusion geofence
   * (#451). The mission returns here, so a home outside the fence is a
   * crossing and the server refuses the save — the same as any waypoint.
   */
  outsideGeofence?: boolean;
}

/**
 * Home-base marker for the planner draft: a map **pin** (not a house glyph, and
 * not a numbered circle) so it reads as the home point on the route and is not
 * mistaken for a waypoint (issue #342). The pin's tip marks the exact location.
 *
 * It is intentionally NOT draggable — the home base is set via the panel
 * controls or a placement click, and the default home tracks waypoint 1. When
 * no custom home is set the pin sits on the start point to show it is home.
 * Colour #0066FF (contrast 5.74:1 on white).
 */
export const PlannerHomeBaseMarker: React.FC<PlannerHomeBaseMarkerProps> = ({
  position,
  isDefault,
  outsideGeofence = false,
}) => {
  const baseLabel = isDefault ? 'Home (start point)' : 'Home base';
  // The state is in the name, not only in the fill (ui/CLAUDE.md).
  const label = outsideGeofence ? `${baseLabel} — outside the geofence` : baseLabel;

  const icon = useMemo(
    () =>
      new DivIcon({
        html: `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 36"
               width="24" height="36" role="img" aria-label="${label}"
               style="filter: drop-shadow(0 1px 2px rgba(0,0,0,0.45));">
            <path fill="${outsideGeofence ? '#d55e00' : '#0066FF'}"
                  stroke="#ffffff" stroke-width="1.5"
                  ${outsideGeofence ? 'stroke-dasharray="3 2"' : ''}
                  d="M12 1C6 1 1.5 5.7 1.5 11.5C1.5 19.5 12 34 12 34S22.5 19.5 22.5 11.5C22.5 5.7 18 1 12 1Z"/>
            ${
              outsideGeofence
                ? '<text x="12" y="15.5" text-anchor="middle" font-size="12" font-weight="bold" fill="#ffffff">!</text>'
                : '<circle cx="12" cy="11.5" r="4.5" fill="#ffffff"/>'
            }
          </svg>
        `,
        className: 'planner-home-base-marker',
        // Tip of the pin sits on the point (24×36 viewBox, tip at x=12,y=34).
        iconSize: [24, 36],
        iconAnchor: [12, 34],
      }),
    [label, outsideGeofence]
  );

  return (
    <Marker position={[position.lat, position.lng]} icon={icon}>
      <Tooltip direction="top" offset={[0, -34]}>
        <div>
          <strong>{baseLabel}</strong>
          <br />
          {outsideGeofence ? (
            <>
              <strong>Outside the geofence — this draft cannot be saved.</strong>
              <br />
            </>
          ) : null}
          {isDefault ? 'Return-to-Home closes the loop here' : null}
          {isDefault ? <br /> : null}
          {formatLatLng(position.lat, position.lng)}
        </div>
      </Tooltip>
    </Marker>
  );
};
